// src/components/AttackVectorsList.tsx
// Extracted Attack Scenarios selection component - Industry-specific threat modeling

import React from 'react';
import { AttackVectorWithControls } from '../types';

interface AttackVectorsListProps {
  attackVectors: AttackVectorWithControls[];
  selectedVector: AttackVectorWithControls | null;
  industry: string;
  currentScores: { [key: string]: number };
  isSimulating: boolean;
  onVectorSelect: (vector: AttackVectorWithControls) => void;
}

const AttackVectorsList: React.FC<AttackVectorsListProps> = ({
  attackVectors,
  selectedVector,
  industry,
  currentScores,
  isSimulating,
  onVectorSelect
}) => {
  const getRiskLevel = (riskScore: number): string => {
    return riskScore > 20 ? 'High' : riskScore > 15 ? 'Medium' : 'Low';
  };

  const getRiskColor = (riskLevel: string): string => {
    return riskLevel === 'High' ? 'var(--ra-danger)' :
           riskLevel === 'Medium' ? 'var(--ra-warning)' : 'var(--ra-success)';
  };

  // Calculate dynamic attack vector scores based on current readiness levels
  const getDynamicRiskScore = (vector: AttackVectorWithControls): number => {
    if (!isSimulating) {
      return vector.current_risk_score || vector.risk_score || 20;
    }

    // Calculate average readiness score
    const readinessValues = Object.values(currentScores);
    const averageReadiness = readinessValues.reduce((sum, score) => sum + score, 0) / readinessValues.length;
    
    // Convert readiness to risk impact factor (inverse relationship)
    // Higher readiness = lower risk multiplier
    const baseRiskScore = vector.original_risk_score || vector.risk_score || 20;
    const readinessImpact = (100 - averageReadiness) / 100; // 0-1 scale
    const riskMultiplier = 0.3 + (readinessImpact * 0.7); // Scale between 0.3 and 1.0
    
    // Apply category-specific readiness impacts
    let categoryMultiplier = 1.0;
    const vectorCategory = vector.category?.toLowerCase() || '';
    
    if (vectorCategory.includes('phishing') || vectorCategory.includes('social')) {
      // Culture and People readiness affect social engineering vectors more
      const socialReadiness = (currentScores.culture + currentScores.people) / 2;
      categoryMultiplier = (100 - socialReadiness) / 100;
    } else if (vectorCategory.includes('ransomware') || vectorCategory.includes('malware')) {
      // Technical Debt and Governance affect technical vectors more
      // For technical_debt, lower score = better (less debt), so use it directly as vulnerability
      const techVulnerability = (currentScores.technical_debt + (100 - currentScores.governance)) / 2;
      categoryMultiplier = techVulnerability / 100;
    } else if (vectorCategory.includes('cloud') || vectorCategory.includes('infrastructure')) {
      // Technical Debt and Third Party affect infrastructure vectors
      // technical_debt: lower = better; third_party: higher = better
      const infraVulnerability = (currentScores.technical_debt + (100 - currentScores.third_party)) / 2;
      categoryMultiplier = infraVulnerability / 100;
    } else if (vectorCategory.includes('insider') || vectorCategory.includes('internal')) {
      // Culture and People readiness affect insider threats
      const insiderReadiness = (currentScores.culture + currentScores.people) / 2;
      categoryMultiplier = (100 - insiderReadiness) / 100;
    } else if (vectorCategory.includes('third') || vectorCategory.includes('vendor')) {
      // Third Party readiness affects vendor risks
      categoryMultiplier = (100 - currentScores.third_party) / 100;
    }

    const dynamicScore = Math.round(baseRiskScore * riskMultiplier * (0.5 + categoryMultiplier * 0.5));
    return Math.max(1, Math.min(50, dynamicScore)); // Keep within reasonable bounds
  };

  return (
    <div style={{ 
      padding: '20px',
      backgroundColor: 'var(--ra-glass-bg)',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: 'var(--ra-text-primary)', margin: 0 }}>
          🎯 Top Attack Scenarios ({industry})
        </h3>
        {isSimulating && (
          <div style={{
            backgroundColor: 'var(--ra-success)',
            color: 'white',
            fontSize: '10px',
            padding: '4px 8px',
            borderRadius: '12px',
            fontWeight: 'bold'
          }}>
            DYNAMIC
          </div>
        )}
      </div>
      <p style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '15px' }}>
        {isSimulating ? 'Dynamic scoring based on readiness levels:' : 'Real threat analysis from assessment:'}
      </p>
      
      {attackVectors.length === 0 ? (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: 'var(--ra-text-secondary)',
          fontSize: '14px'
        }}>
          No attack vectors available for analysis
        </div>
      ) : (
        attackVectors.map((vector, index) => {
          const dynamicRiskScore = getDynamicRiskScore(vector);
          const riskLevel = getRiskLevel(dynamicRiskScore);
          const riskColor = getRiskColor(riskLevel);
          const isSelected = selectedVector?.id === vector.id;
          
          return (
            <div 
              key={vector.id || index} 
              onClick={() => onVectorSelect(vector)}
              style={{ 
                marginBottom: '15px',
                padding: '12px',
                border: isSelected ? '2px solid #6f42c1' : '1px solid var(--ra-glass-border)',
                borderRadius: '8px',
                backgroundColor: isSelected ? 'rgba(111, 66, 193, 0.08)' : 'var(--ra-deep-blue)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isSelected ? '0 0 10px rgba(111, 66, 193, 0.3)' : 'none'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 5px 0', color: 'var(--ra-text-primary)', fontSize: '14px' }}>
                    #{index + 1} {vector.name}
                    {isSelected && (
                      <span style={{ marginLeft: '8px', color: '#6f42c1' }}>
                        ← Selected
                      </span>
                    )}
                  </h4>
                  <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)' }}>
                    <span>Likelihood: {vector.likelihood}/5</span>
                    <span style={{ marginLeft: '15px' }}>Impact: {vector.impact}/5</span>
                    {vector.category && (
                      <span style={{ marginLeft: '15px' }}>Category: {vector.category}</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: riskColor 
                  }}>
                    {dynamicRiskScore}
                  </div>
                  <div style={{ 
                    fontSize: '10px', 
                    color: riskColor,
                    textTransform: 'uppercase'
                  }}>
                    {riskLevel}
                  </div>
                </div>
              </div>
              
              {/* Show risk change if simulation is active or different from original */}
              {(isSimulating || (vector.original_risk_score && vector.original_risk_score !== vector.current_risk_score)) && (
                <div style={{ 
                  marginTop: '8px',
                  fontSize: '11px',
                  color: 'var(--ra-text-secondary)',
                  fontStyle: 'italic'
                }}>
                  Originally: {vector.original_risk_score || vector.risk_score || 20} → 
                  <span style={{ color: riskColor, fontWeight: 'bold' }}>
                    {' '}{dynamicRiskScore}
                  </span>
                  {' '}({dynamicRiskScore < (vector.original_risk_score || vector.risk_score || 20) ? '↓' : '↑'})
                  {isSimulating && (
                    <span style={{ color: 'var(--ra-success)', marginLeft: '5px' }}>
                      (Dynamic)
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default AttackVectorsList;